// Shared Header — Bookflow variant (no navigation)
// Bookflow_Links ship `hidden`; they are revealed only on Customise
// (`customise=true`), and only then is the CSRF token + shortlist fetched.
// Trust_Element modals are bound on every Bookflow page.

import { applyBookflowVisibility } from './customise-flag';
import { ShortlistUIUpdater } from './shared-header-core';
import type { ShortlistSummary } from './shared-header-core';
import { initTrustElements } from './trust-elements';

const CSRF_URL = `/results/vto/search/csrf/token`;
const SHORTLIST_URL = `/results/vto/search/shortlists`;

async function fetchShortlist(updater: ShortlistUIUpdater): Promise<void> {
  try {
    const csrfResponse = await fetch(CSRF_URL, { credentials: 'include' });
    if (!csrfResponse.ok) return;

    const csrf: { token: string; headerName: string } = await csrfResponse.json();

    const shortlistResponse = await fetch(SHORTLIST_URL, {
      credentials: 'include',
      headers: { [csrf.headerName]: csrf.token },
    });
    if (!shortlistResponse.ok) return;

    const shortlist: ShortlistSummary = await shortlistResponse.json();
    updater.update(shortlist.id, shortlist.accommodationGiataIds.length);
  } catch {
    // Silently fail — shortlist link stays hidden by default
  }
}

export interface BookflowHeaderDeps {
  doc: Document;
  location: Pick<Location, 'search'>;
  fetchShortlist: (updater: ShortlistUIUpdater) => Promise<void>;
  initTrustElements: (headerEl: HTMLElement) => void;
}

const defaultDeps = (): BookflowHeaderDeps => ({
  doc: document,
  location: window.location,
  fetchShortlist,
  initTrustElements: (headerEl) => initTrustElements(headerEl),
});

/** Req 1.8: an unreadable search string reads as "not Customise", so links stay hidden. */
function readSearch(location: Pick<Location, 'search'>): string {
  try {
    return location.search;
  } catch {
    return '';
  }
}

export function initBookflowHeader(overrides: Partial<BookflowHeaderDeps> = {}): void {
  const deps: BookflowHeaderDeps = { ...defaultDeps(), ...overrides };
  const headerEl = deps.doc.querySelector<HTMLElement>('[data-header]');
  if (!headerEl) return;

  const revealed = applyBookflowVisibility(headerEl, readSearch(deps.location)); // once (Req 1.9)
  if (revealed) void deps.fetchShortlist(new ShortlistUIUpdater(headerEl));
  deps.initTrustElements(headerEl);
}

const onLoad = (): void => initBookflowHeader();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', onLoad);
} else {
  onLoad();
}
